import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { Organization } from '../../organization/dto/Organization';
import { takeUntil } from 'rxjs/operators';
import { OrganizationsService } from '../../organization/components/service/organizations.service';
import { ModalButtons } from '../../shared/component/modal-container/modal-container.component';
import { NotImplementedException, ValidationException } from '../../../util/error/typed-error';
import { NOTIFICATIONS, NotificationsService } from '../../../core/service/notifications.service';
import {
  DefaultBulkUploadIs,
  DefaultBulkUploadShow,
  DefaultBulkUploadShowValidationMessage,
} from '../pojo/default-bulk-upload-pojo';
import * as XLSX from 'xlsx-js-style';

@Component({
  selector: 'app-default-bulk-upload',
  templateUrl: './default-bulk-upload.component.html',
  styleUrls: ['./default-bulk-upload.component.scss'],
})
export class DefaultBulkUploadComponent implements OnInit, OnDestroy {
  protected _destroyStream: Subject<void> = new Subject<void>();
  protected uploadCounter = 0;

  @Output() closeEmitter = new EventEmitter<unknown>();
  selectedOrg: Organization;
  show: DefaultBulkUploadShow;
  is = new DefaultBulkUploadIs();

  currentButtonType = {
    download: 'accent',
    upload: 'grey-white',
  };

  modalButtons: ModalButtons = {
    close: true,
  };

  constructor(
    protected orgService: OrganizationsService,
    protected notificationService: NotificationsService) {
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  ngOnInit(): void {
    this.initVariables();
    this.initSubscriptions();
  }

  /**
   * Closes the modal by emitting some value.
   * By default, the value is boolean, but it can be changed (see {@link MetricBulkUploadComponent})
   *
   * @param value - The value to be emitted when closing the emitter.
   */
  close(value: unknown): void {
    this.closeEmitter.emit(value);
  }

  /**
   * Closes the modal as unsuccessful (e.x. cross button).
   * Can be overridden in case different return type is expected.
   */
  protected closeAsFailed(): void {
    this.close(false);
  }

  /**
   * [!] Must be overridden, in order to download the Excel from a Template class.
   *
   * Sets the buttons to the next state, when a user has to upload the downloaded template.
   * Notifies about successful start of downloading.
   *
   * Example:
   * <pre>
   *  override downloadExcel(): void {
   *     new CompanyBulkUploadTemplate().export(this.companies);
   *     super.downloadExcel();
   *   }
   * </pre>
   */
  protected downloadExcel(): void {
    this.currentButtonType.download = 'grey-white';
    this.currentButtonType.upload = 'accent';
    this.notificationService.success(NOTIFICATIONS.BULK_UPLOAD_DOWNLOAD_STARTED_SUCCESS);
  }

  /**
   * Default method which reads the file and then parses the Excel file.
   *
   * @param file is the user-provided Excel file.
   */
  protected upload(file: HTMLInputElement): void {
    this.show.validationMessage = undefined;
    const reader: FileReader = new FileReader();
    reader.readAsBinaryString(file.files[0]);
    reader.onload = async (e: ProgressEvent<FileReader>): Promise<void> => {
      const binary = e.target?.result as string;
      const wb: XLSX.WorkBook = XLSX.read(binary, { type: 'binary' });
      const ws: XLSX.WorkSheet = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws);
      file.value = null; // reset file input, so it is possible to upload the same file in case of some error.
      await this.parseDataAfterImport(data);
      this.afterUpload();
    };
  }

  /**
   * [!] Must be overridden.
   *
   * Writes into a local variable the parsed data from the Template class.
   *
   * Example:
   * <pre>
   * override parseDataAfterImport(data: unknown[]): void {
   *    this.parsedCompanies = new CompanyBulkUploadTemplate().import(data);
   * }
   * </pre>
   *
   * @param data - The data to be parsed.
   * @throws {NotImplementedException} - Throws an error indicating that the method is not implemented.
   */
  protected parseDataAfterImport(data: unknown[]): Promise<void> {
    console.warn(data);
    throw new NotImplementedException('parseDataAfterImport method is not implemented');
  }

  /**
   * [!] Must be overridden.
   *
   * After a user uploads the Excel template and clicks continue, the main action is going to take the place.
   *
   * @hint write {@code this.is.applyActivated = false;} at the function start.
   * This will disable the apply button, so that user cannot type multiple times.
   *
   */
  protected confirm(): Promise<void> {
    throw new NotImplementedException('confirm method is not implemented');
  }

  protected afterUpload(): void {
    this.is.dataUploaded = true;
    this.validateData();
    this.assertSufficientData();
    this.mapData();
    this.updateModalButtonsToContinue();
  }

  /**
   * [!] Must be overridden.
   *
   * Makes checks if the provided Excel data is valid.
   *
   * @hint use {@code checkError()} function.
   */
  protected validateData(): void {
    throw new NotImplementedException('validateData method is not implemented');
  }

  /**
   * [!] Must be overridden.
   *
   * After the data is parsed, we want to make sure, that there is at least some data to proceed with.
   * This function makes such a check.
   *
   * @hint if there is no sufficient data, use {@code this.throwInsufficientDataException();}.
   */
  protected assertSufficientData(): void {
    throw new NotImplementedException('assertSufficientData method is not implemented');
  }

  protected showInsufficientDataAndClose(): void {
    this.notificationService.warning(NOTIFICATIONS.BULK_UPLOAD_NO_DATA_WARNING);
    this.close(false);
  }

  /**
   * [!] Must be overridden.
   *
   * After we receive the partially prepared data from the parsed Excel file (via a Template),
   * we need to prepare the data for the next CRUD operations.
   * The best way is to have the final data version in the {@private show} variable.
   */
  protected mapData(): void {
    throw new NotImplementedException('mapData method is not implemented');
  }

  /**
   * After user uploaded the Excel file, and this file was valid,
   * user can now click 'Continue' to proceed the bulk upload.
   */
  protected updateModalButtonsToContinue(): void {
    this.modalButtons = {
      close: true,
      apply: 'Continue',
    };
    this.is.applyActivated = true;
  }

  /**
   * In case of non-empty @param data, creates the error message template.
   * Afterward, prepares the unique validation error
   * and finishes the validation process by notifying user + showing validation error in html.
   *
   * @param data which is invalid. Can be empty: then nothing happens.
   * @param message is the error message to show in the bulk upload modal.
   * @param bottomText is optional and will overwrite the default bottom text with amount of wrong data.
   */
  protected checkError(data: unknown[], message: string, bottomText?: string): void {
    if (!data.length) {
      return;
    }
    let andMoreText = '';
    if (data.length > 1) {
      andMoreText = ` and ${data.length - 1} more.`;
    }
    const validationMessage: DefaultBulkUploadShowValidationMessage = {
      upperText: message,
      bottomText: bottomText ?? andMoreText,
    };
    this.prepareValidationError(data[0], validationMessage);
    this.notifyErrorAndClose(validationMessage);
  }

  /**
   * [!] Must be overridden.
   *
   * Update the message of the validation error with the correct data. This error will be shown in html.
   *
   * Example:
   * <pre>
   *  override prepareValidationError(data: PortfolioPerformance, message: DefaultBulkUploadShowValidationMessage): void {
   *     message.bottomText = `${data.name}, ${data.period}${message.bottomText}`;
   *   }
   * </pre>
   *
   * @param data of bulk upload.
   * @param message already initialized variable which has to be updated.
   *                Already contains the upper text (what is expected or what is wrong).
   *                Already contains the bottom text (if there are more than 1 wrong entity).
   */
  protected prepareValidationError(data: unknown, message: DefaultBulkUploadShowValidationMessage): void {
    console.warn(data, message);
    throw new NotImplementedException('proceedValidationError method is not implemented');
  }

  /**
   * Notifies the user about an error both in the modal,
   * and in notification bar on the right.
   * In order to stop the next logic to occur, throws an Exception.
   *
   * @param message to display in the modal.
   */
  protected notifyErrorAndClose(message: DefaultBulkUploadShowValidationMessage): void {
    this.notificationService.error({ title: 'Validation error', message: '' });
    this.show.validationMessage = message;
    throw new ValidationException('Validation error');
  }


  protected initSubscriptions(): void {
    this.orgService.selected$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((org) => {
        if (!org) {
          return;
        }
        this.selectedOrg = org;
        this.fetchDataAfterOrgInit();
      });
  }

  /**
   * InitSubscription fetches by default the selected organization.
   * Instead of overriding the whole initSubscription, it is possible to override fetchDataAfterOrgInit.
   * In this function the {@private selectedOrg} is already provided.
   */
  protected fetchDataAfterOrgInit(): void {}

  /**
   * [!] Must be overridden.
   *
   * Initialization of variables.
   */
  protected initVariables(): void {
    this.show = new DefaultBulkUploadShow();
    this.show.title = {
      header: 'Some header',
      info: {
        main: 'Your data is very important',
        sub: 'Therefore you are important :heart:',
      },
    };
  }

  /**
   * There is a counter of how many entities have been uploaded.
   * Increments the counter, updates the title (to show how many have been already uploaded),
   * and then if the last has been reached: notifies about success and closes the modal.
   */
  protected addCounterAndMakeCheck(): void {
    this.uploadCounter++;
    this.show.title.header = this.getTitle();

    if (this.isLastElementUploaded()) {
      this.onSuccessNotifyAndClose();
    }
  }

  /**
   * [!] Must be overridden.
   *
   * Provides the title for the modal.
   * Might contain different states (see {@link ModalCompanyBulkUploadComponent}).
   */
  protected getTitle(): string {
    throw new NotImplementedException('getTitle method is not implemented');
  }

  /**
   * [!] Must be overridden.
   *
   * Simple function which is needed for the counter to check, if the last element has been succeeded.
   *
   * Example:
   * <pre>
   * override isLastElementUploaded(): boolean {
   *     return this.show.parsedCompanies.length === this.uploadCounter;
   * }
   * </pre>
   */
  protected isLastElementUploaded(): boolean {
    throw new NotImplementedException('isLastElementUploaded method is not implemented');
  }

  protected onSuccessNotifyAndClose(): void {
    this.notificationService.success({
      message: '',
      title: this.uploadCounter + ` ${this.uploadCounter === 1 ? 'entity' : 'entities'} uploaded successfully`,
    });
    this.close(true);
  }
}
